import { memo, useContext, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

import UserPreferencesContext from '../../../api/userPreferences/context/context';
import { DisplayZoom, DISPLAY_ZOOM_OPTIONS } from '../../../api/userPreferences/types';

const percent = (zoom: number) => `${Math.round(zoom * 100)}%`;

const STEP_SX = { width: 26, height: 26, '& svg': { width: '16px !important', height: '16px !important' } };

/**
 * Display size — the whole app scaled like the browser's own zoom, per user
 * (see useDisplayZoom). The button shows the current size instead of an icon;
 * the popover steps it down or up.
 */
const HeaderDisplaySize = () => {
    const { preferences, update } = useContext(UserPreferencesContext);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const zoom: DisplayZoom = preferences.displayZoom ?? 1;
    const index = DISPLAY_ZOOM_OPTIONS.indexOf(zoom);
    const setZoom = (next: DisplayZoom) => update({ displayZoom: next });

    return (
        <>
            <Tooltip title="Display size" arrow>
                <IconButton
                    color="inherit"
                    size="medium"
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    aria-label={`Display size, ${percent(zoom)}`}
                >
                    {/* Same box as the other header icons, so the row keeps one rhythm. */}
                    <Box
                        component="span"
                        sx={{
                            width: 24,
                            height: 24,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.66rem',
                            fontWeight: 600,
                            letterSpacing: '-0.02em',
                            fontVariantNumeric: 'tabular-nums'
                        }}
                    >
                        {percent(zoom)}
                    </Box>
                </IconButton>
            </Tooltip>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{ sx: { mt: 0.75, borderRadius: '12px', px: 1, py: 0.75 } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                        size="small"
                        sx={STEP_SX}
                        disabled={index <= 0}
                        onClick={() => setZoom(DISPLAY_ZOOM_OPTIONS[index - 1])}
                        aria-label="Smaller"
                    >
                        <RemoveIcon />
                    </IconButton>
                    <Typography
                        sx={{
                            minWidth: 44,
                            textAlign: 'center',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            fontVariantNumeric: 'tabular-nums'
                        }}
                    >
                        {percent(zoom)}
                    </Typography>
                    <IconButton
                        size="small"
                        sx={STEP_SX}
                        disabled={index >= DISPLAY_ZOOM_OPTIONS.length - 1}
                        onClick={() => setZoom(DISPLAY_ZOOM_OPTIONS[index + 1])}
                        aria-label="Larger"
                    >
                        <AddIcon />
                    </IconButton>
                </Box>
                <ButtonBase
                    onClick={() => setZoom(1)}
                    disabled={zoom === 1}
                    sx={{
                        display: 'block',
                        width: '100%',
                        mt: 0.25,
                        py: 0.25,
                        borderRadius: '6px',
                        fontSize: '0.72rem',
                        color: 'text.secondary',
                        '&.Mui-disabled': { opacity: 0.4 },
                        '&:hover': { bgcolor: 'action.hover' }
                    }}
                >
                    Reset
                </ButtonBase>
            </Popover>
        </>
    );
};

export default memo(HeaderDisplaySize);
